import {GoogleMapsOverlay} from '@deck.gl/google-maps';
import {GeoJsonLayer, TextLayer, IconLayer} from '@deck.gl/layers';
import type {Layer, Color} from '@deck.gl/core';

import {load} from '@loaders.gl/core';
import {MVTLoader} from '@loaders.gl/mvt';

import {colorToArray, pSBC} from '@/services/GraphicService/colorUtilities';
import {createMVTLayer, createDeckglOverlay, pickMultipleObjects} from '@/services/MapService/Deckgl';
import {getLayerStyle} from '@/services/GraphicService/VectorTileGraphic/featurePaint';
import {getFilterSignature, getTileFeatures} from '@/services/MapService/MVTLayers/tileFeatureCache';
import {matchesLayerFilters} from '@/lib/filters/layers/layerFilters';
import {TokenService} from '@/services/TokenService';
import type {DrawUpdateTriggers, MVTFeature, MVTLayerConfig} from '@/services/MapService/MVTLayers/types';

let _googleMapsOverlay: GoogleMapsOverlay | null = null;
const _layerData: Map<string, MVTLayerConfig> = new Map();

/** Layers is an array where index determines draw order. Lower index means it is drawn lower. */
let layers: Layer[] = [];

const getDeckglOverlayView = (): GoogleMapsOverlay => {
  if (!_googleMapsOverlay) {
    throw new Error('GoogleMapsOverlay is not initialized. Call MVTLayers.initialize(map) first.');
  }
  return _googleMapsOverlay;
};

// BMAPS-2783: When we are using sublayer `idField` for all feature id keys,
// we shouldn't need this function at all anymore because it should be passed when creating the layer.
/**
 * Builds the matcher once per draw.
 */
const createFeatureIdMatcher = (id: number | null, featureIdKey: string) => {
  if (id === null || id === undefined) return () => false;
  // check several permutations of the featureIdKey and then 'id'
  const keys = [...new Set([featureIdKey, featureIdKey.toUpperCase(), featureIdKey.toLowerCase(), 'id'])];
  const target = String(id);
  return (featureProperties) => keys.some((key) => String(featureProperties[key]) === target);
};

/**
 * Scales a fill color's alpha by the layer's opacity setting.
 */
const scaleFillAlpha = (color: Color | null, fillOpacity: number | undefined): Color | null => {
  if (fillOpacity === undefined || !color) return color;
  const [r, g, b, a = 255] = color;
  return [r, g, b, Math.round(a * fillOpacity)];
};

/**
 * Convenience object for creating deck.gl MVTLayers
 */
const MVTLayers = {
  /**
   * Initialize the GoogleMapsOverlay
   */
  initialize(map: google.maps.Map) {
    if (!map) {
      throw new Error('Map must be provided when initializing deck.gl GoogleMapsOverlay');
    }
    if (!_googleMapsOverlay) {
      _googleMapsOverlay = createDeckglOverlay({});
      _googleMapsOverlay.setMap(map);
    } else {
      console.warn('GoogleMapsOverlay is already initialized.');
    }
  },
  /**
   * Creates a new MVTLayer and adds it to the map.
   */
  create(layerConfig: MVTLayerConfig) {
    if (!layerConfig.id) {
      throw new Error('MVTLayerProps must have an id');
    }
    if (!layerConfig.data) {
      throw new Error('MVTLayerProps must have data');
    }
    _layerData.set(layerConfig.id, layerConfig);
    this.drawTileLayer(layerConfig.id);
  },
  /**
   * Draws an MVTLayer on the map.
   * @param id The layer ID
   * @param drawUpdateTriggers An object containing dependencies to trigger a redraw of the layer.
   */
  drawTileLayer(id: string, {
    visible = true,
    visibleLayers = [],
    legendGroups = [],
    selectedFeatureId = null,
    fillOpacity = undefined,
    activeFilters = {},
  }: DrawUpdateTriggers = {},
  ) {
    const layerData = _layerData.get(id);
    if (!layerData || !layerData.styleJson) {
      console.warn(`Layer data or style json for id "${id}" not found.`);
      return;
    }

    const filterSignature = getFilterSignature(visibleLayers, legendGroups, activeFilters);
    // HACK: This is a workaround to find out if the layer is a text layer. '/label' is a custom suffix that we
    // use to identify text layers in the style JSON. Publishers of the layer must add it to the layer name and
    // are directed to do so via the documentation.
    const isTextLayer = layerData.styleJson.layers.some((l) => l['source-layer']?.includes('/label'));
    const isIconLayer = layerData.iconUrls?.iconAtlas && layerData.iconUrls?.iconMapping;

    let layer: Layer;
    try {
      const matchesSelectedFeature = createFeatureIdMatcher(selectedFeatureId, layerData.featureIdKey);
      const {paint, allowedGeometryTypes} = getLayerStyle(layerData.styleJson);
      layer = createMVTLayer({
        id,
        data: layerData.data,
        // Only the selected feature converts a color here. Every other feature reads the array that
        // featurePaint already built and cached against it.
        getFillColor: (d) => {
          const paintValues = paint(d);
          const color = matchesSelectedFeature(d.properties)
            ? colorToArray(pSBC(0.5, paintValues?.color))
            : paintValues?.colorArray ?? null;
          return scaleFillAlpha(color as Color, fillOpacity) as Color;
        },
        getLineColor: (d) => {
          const paintValues = paint(d);
          if (matchesSelectedFeature(d.properties)) {
            return colorToArray(pSBC(0.5, paintValues?.strokeColor || paintValues?.color)) as Color;
          }
          return (paintValues?.strokeColorArray ?? null) as Color;
        },
        getLineWidth: (d) => {
          const paintValues = paint(d);
          let width = paintValues?.width;
          // BMAPS-2783: We shouldn't hardcode values for layers, they should be edited in the esri vector style editor
          // when all layers are in schema version 2, apply custom styling in the style editor to avoid these defaults.
          // TEMP: Clamp the width to a minimum of (double whatever is defined in the Esri style)
          if (Number.isFinite(width) && (d.geometry.type === 'LineString' || d.geometry.type === 'MultiLineString')) {
            width *= 2;
          }
          return width;
        },
        getPointRadius: (d) => {
          const paintValues = paint(d);
          let radius = paintValues?.radius;
          // BMAPS-2783: We shouldn't hardcode values for layers, they should be edited in the esri vector style editor
          // when all layers are in schema version 2, apply custom styling in the style editor to avoid these defaults.
          // TEMP: Clamp the radius to a minimum of 5
          if (radius < 5) {
            radius = 5;
          }
          return radius;
        },
        minZoom: layerData.minZoom,
        maxZoom: layerData.maxZoom,
        zoomOffset: 1,
        renderSubLayers: (props) => {
          if (!visible) return null;
          if (!props.data) return null;

          const cache = getTileFeatures(props.tile, props.data as MVTFeature[], filterSignature, (d) => {
            const allowedTypes = allowedGeometryTypes.get(d.properties?.layerName);
            if (!allowedTypes?.has(d.geometry?.type)) return false;
            if (!visibleLayers.includes(d.properties?.layerName)) return false;
            if (!matchesLayerFilters(d, activeFilters)) return false;
            if (!legendGroups.length) return true; // If there are no legend groups, show all features
            return legendGroups.some((group) => group.matchesLegendItem(d));
          });

          const layers: Layer[] = [];

          // Non-icon GeoJson fallback
          if (!isTextLayer && !isIconLayer && cache.filtered.length) {
            layers.push(new GeoJsonLayer({
              ...props,
              data: cache.filtered,
            }));
          }
          const createTextLayers = () => {
            const nonPoints = cache.nonPoints ??= cache.filtered.filter((d: MVTFeature) => d.geometry.type !== 'Point');
            const textPoints = cache.textPoints ??= cache.filtered.filter((d: MVTFeature) => d.geometry.type === 'Point');
            const geoLayer = new GeoJsonLayer({
              ...props,
              // @ts-ignore MVTFeature doesn't entirely overlap with the type for GeoJsonLayer
              data: nonPoints,
            });

            const textLayer = new TextLayer({
              ...props,
              id: `${props.id}-text-layer`,
              data: textPoints,
              getText: (d) => {
                return d.properties?._name || '';
              },
              getSize: (d) => {
                const paintValues = paint(d);
                return paintValues?.['text-size'] ?? 12;
              },
              getColor: (d) => {
                const paintValues = paint(d);
                return (paintValues?.textColorArray ?? [0, 0, 0, 255]) as Color;
              },
              getPosition: (d) => {
                return d.geometry?.coordinates ?? [0, 0];
              },
            });
            return [geoLayer, textLayer];
          };

          const createIconLayers = () => {
            const iconGeometry = cache.iconGeometry ??= cache.filtered.filter((d: MVTFeature) => (
              (d.geometry.type === 'LineString' ||
                d.properties?._symbol === 0 ||
                d.properties?._symbol === 1) &&
                !d.properties?.uniqueVal),
            );
            const points = cache.points ??= cache.filtered.filter((d) =>
              d?.geometry?.type.includes('Point') ||
              d?.geometry?.type.includes('MultiPoint'));
            const geoLayer = new GeoJsonLayer({
              ...props,
              // @ts-ignore MVTFeature doesn't entirely overlap with the type for GeoJsonLayer
              data: iconGeometry,
            });
            const iconLayer = new IconLayer({
              ...props,
              id: `${props.id}-${props.tile.id}-icons`,
              data: points,
              getIcon: (d) => {
                // If the IconLayer has Unique Value symbology
                if (d.properties?.uniqueVal) {
                  return `${d.properties.layerName}/${d.properties.uniqueVal}`;
                }
                // If the IconLayer has a single value symbology
                return `${d.properties.layerName}`;
              },
              getPosition: (d) => d.geometry.coordinates,
              getSize: (d) => matchesSelectedFeature(d.properties) ? 32 : 22,
              // Only reached when isIconLayer is set, which requires both of these.
              iconAtlas: layerData.iconUrls!.iconAtlas,
              iconMapping: layerData.iconUrls!.iconMapping,
              updateTriggers: {
                getSize: [selectedFeatureId],
              },
              pickable: true,
            });

            return [geoLayer, iconLayer];
          };

          if (isTextLayer) return createTextLayers();

          if (isIconLayer) {
            const [geoLayer, iconLayer] = createIconLayers();
            layers.push(geoLayer);
            layers.push(iconLayer);
          }
          return layers;
        },
        updateTriggers: {
          getFillColor: [selectedFeatureId, fillOpacity],
          getLineColor: [selectedFeatureId],
          renderSubLayers: [visibleLayers, legendGroups, activeFilters],
        },
        visible,
        fetch: async (url, context) => {
          if (layerData.isPublicLayer === false) {
            const token = await TokenService.getToken('arcGisServerToken');
            context.loadOptions = {fetch: {headers: {'Authorization': `Bearer ${token}`}}};
          }
          if (url.includes('?&token=${token}')) {
            url = url.replace('?&token=${token}', '');
          }
          return load(url, [MVTLoader], context.loadOptions);
        },
      });
    } catch (err) {
      // Log the error and fall back to a no-op layer so the map rendering pipeline remains stable.
      // This prevents the rest of the layer stack from failing if one layer creation errors.
      console.error(`Error creating MVT layer "${id}":`, err);
      layer = new GeoJsonLayer({id: `${id}-error`, data: [], visible: false});
    }

    // Here we see if the layer already exists in the layers array.
    // If it does exist, we replace it with a map. If it does not, we create a new array.
    // We do not use .splice() here because setProps will not trigger a redraw.
    // BMAPS-2852: When changing z-indexing of layers, we will need to extend this functionality
    // and perhaps give it its own function for changing the order of layers given an id.
    const existingLayer = layers.find((l) => l.id === id);
    let newLayers: Layer[];

    if (existingLayer) {
      if (!existingLayer.props.visible && visible) {
        newLayers = [...layers.filter((l) => l.id !== id), layer];
      } else {
        newLayers = layers.map((l) => l.id === id ? layer : l);
      }
    } else {
      newLayers = [...layers, layer];
    }

    const overlay = getDeckglOverlayView();
    overlay.setProps({layers: newLayers});
    layers = newLayers;
  },
  /**
   * Gets all features for a layer at a click position.
   */
  getLayerFeaturesAtPosition(id: string, {x, y}: {x: number; y: number}) {
    const overlay = getDeckglOverlayView();
    return pickMultipleObjects({
      overlay,
      x,
      y,
      radius: 1,
      depth: Infinity,
      layerIds: [id],
    });
  },
  getLayers() {
    return layers;
  },
};

export {
  MVTLayers,
  getDeckglOverlayView,
};
